import { useCallback, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent } from 'react';

import { useT } from '../../../../i18n';
import { gonSo } from '../nghiep-vu/so-thong-ke';
import type { LoaiToken } from '../nghiep-vu/types-vet';

/**
 * Biểu đồ SVG thuần cho hai màn số liệu (Tổng quan, Sử dụng AI) — port `thong-ke/bieu-do.tsx` native, đưa vào
 * `chung/` vì hai màn dùng. Kho không có thư viện biểu đồ (CLAUDE.md: không thêm thư viện UI), và vài chục hình chữ
 * nhật không đáng một gói 200–450 KB.
 *
 * Ba luật giữ y của hai tệp ấy:
 * • Màu qua `var(--color-*)`, không hex — biến CSS tự đổi theo sáng/tối.
 * • Giá trị chính xác hiện ở THẺ CHÚ THÍCH khi rê vào cột, vì biểu đồ nói xu
 *   hướng và bỏ mất số. Không dùng `<title>`: trình duyệt vẽ nó thành một khung
 *   đen một dòng, không căn được số và không đổi theo sáng/tối. Câu đầy đủ cho
 *   trình đọc màn hình nằm ở `<desc>`.
 * • `role="img"` + `aria-label` do nơi gọi truyền (nơi ấy có `useT()`).
 *
 * Các component ở đây KHÔNG dịch chữ: mọi chữ vào qua props. Chúng chỉ đọc `locale` của `useT()` để định dạng số
 * (`1.234` hay `1,234`, ADR-UI-0010).
 */

/**
 * Màu bảy loại token — thứ tự của `LOAI_TOKEN`.
 *
 * Gán theo LOẠI chứ không theo chỉ số như `BieuDo.tsx` của Trợ lí AI: ở đó loạt
 * là dữ liệu của khách nên màu phải suy từ vị trí, còn bảy loại token là một
 * danh sách CỐ ĐỊNH do QĐ-14 chốt. Màu ổn định cho phép người đọc nhận ra lát
 * "ghi đệm" giữa hai biểu đồ, hai kỳ, hai lần mở trang.
 *
 * Hai loại họ "vào đệm" và "ghi đệm" cố ý KHÔNG cùng tông: chúng là hai vế
 * ngược dấu của công thức `tiet_kiem_dem` (QĐ-15), nên nhìn giống nhau là mời
 * người đọc cộng chúng lại.
 */
export const MAU_TOKEN: Record<LoaiToken, string> = {
  dau_vao: 'var(--color-sky-600)',
  dau_vao_anh: 'var(--color-violet-600)',
  dau_vao_tool: 'var(--color-orange-600)',
  dau_vao_dem: 'var(--color-teal-600)',
  ghi_dem: 'var(--color-warning-600)',
  dau_ra: 'var(--color-primary-600)',
  suy_luan: 'var(--color-success-600)',
};

export const MAU_TIEN = 'var(--color-primary-600)';
export const MAU_DUONG = 'var(--color-success-600)';
export const MAU_AM = 'var(--color-danger-600)';

/** Một dòng của thẻ chú thích. `giaTri` đã định dạng sẵn, kèm đơn vị — nơi gọi mới biết đơn vị. */
export interface DongThe {
  nhan: string;
  giaTri: string;
  mau?: string;
  /** Số phụ đứng sau giá trị, ví dụ tỷ lệ. */
  phu?: string;
  /** Dòng tổng. */
  dam?: boolean;
}

const KHE_THE = 10;

/** Đoạn ngang mà thẻ phải tránh (cột đang rê), và điểm dọc muốn thẻ canh giữa nếu có (lát vành). */
interface NeoThe {
  trai: number;
  phai: number;
  y?: number;
}

/**
 * Chỗ đặt thẻ trong khung `rong × cao` — hàm thuần để đọc được luật:
 *
 * 1. Đứng CẠNH cột, không đè lên: bên phải cột ở nửa trái biểu đồ, bên trái ở nửa phải; phía ưu tiên thiếu chỗ thì
 *    lật sang phía kia.
 * 2. Cả hai phía đều thiếu chỗ (khung hẹp ở 768px, cột ở giữa): xuống dưới khung, kẹp ngang vào trong khung — che
 *    tạm chú giải còn hơn che chính cột đang đọc, hay bị cắt ở mép.
 * 3. Chiều dọc: canh từ đỉnh vùng vẽ (hoặc giữa điểm neo), kẹp để không thò khỏi đáy khung; thẻ cao hơn khung thì
 *    đứng từ đỉnh và chỉ thò xuống dưới — phía dưới biểu đồ là chú giải, không có gì cắt.
 */
export function viTriThe(
  neo: NeoThe,
  the: { w: number; h: number },
  /** `trai`: mép trái của miền được đứng (mặc định 0) — âm khi thẻ được phép ra ngoài khung về bên trái. */
  khung: { trai?: number; rong: number; cao: number },
): { left: number; top: number } {
  const mepTrai = khung.trai ?? 0;
  const giua = (neo.trai + neo.phai) / 2;
  const benPhai = neo.phai + KHE_THE;
  const benTrai = neo.trai - KHE_THE - the.w;
  const vuaPhai = benPhai + the.w <= khung.rong;
  const vuaTrai = benTrai >= mepTrai;
  const uuTienPhai = giua - mepTrai < (khung.rong - mepTrai) / 2;
  const tren = neo.y === undefined ? LE_TREN : neo.y - the.h / 2;
  const top = kep(tren, 0, Math.max(0, khung.cao - the.h));
  if (uuTienPhai ? vuaPhai : !vuaTrai && vuaPhai) return { left: benPhai, top };
  if (vuaTrai) return { left: benTrai, top };
  // Hai bên đều chật: xuống DƯỚI khung (đè lên chú giải chứ không đè lên cột đang rê), canh giữa theo cột.
  return { left: kep(giua - the.w / 2, mepTrai, Math.max(mepTrai, khung.rong - the.w)), top: khung.cao + KHE_THE / 2 };
}

const kep = (gt: number, thap: number, cao: number) => Math.min(Math.max(gt, thap), cao);

/**
 * Thẻ chú thích của mục đang rê hoặc đang focus. Đo chính nó lúc gắn vào DOM rồi mới hiện (ref gọi lại, cập nhật
 * trước khi vẽ), nên `viTriThe` đặt theo kích thước thật chứ không đoán. Nơi gọi đặt `key` theo mục để mỗi mục đo lại.
 *
 * `aria-hidden`: câu đầy đủ cho trình đọc màn hình đã ở `<desc>` và `aria-label` của từng cột.
 */
export function TheChuThich({
  tieuDe,
  dong,
  neo,
  khung,
  datCho,
}: {
  tieuDe: string;
  dong: DongThe[];
  neo?: NeoThe;
  khung?: { trai?: number; rong: number; cao: number };
  /** Luật đặt riêng cho mục không phải cột (dòng xếp hạng ngang), thay `neo` + `khung`. */
  datCho?: (the: { w: number; h: number }) => { left: number; top: number };
}) {
  const [kichThuoc, setKichThuoc] = useState<{ w: number; h: number } | null>(null);
  const doThe = useCallback((el: HTMLDivElement | null) => {
    if (el) setKichThuoc({ w: el.offsetWidth, h: el.offsetHeight });
  }, []);
  const vi = !kichThuoc
    ? { left: 0, top: 0 }
    : datCho
      ? datCho(kichThuoc)
      : neo && khung
        ? viTriThe(neo, kichThuoc, khung)
        : { left: 0, top: 0 };
  return (
    <div
      ref={doThe}
      aria-hidden
      style={{ left: vi.left, top: vi.top }}
      className={
        'pointer-events-none absolute z-10 w-max min-w-[11rem] max-w-[18rem] rounded-lg border border-default ' +
        'bg-elevated px-3 py-2.5 shadow-lg motion-safe:transition-opacity motion-safe:duration-100 ' +
        (kichThuoc ? 'opacity-100' : 'invisible opacity-0')
      }
    >
      <p className="mb-2 border-b border-default pb-1.5 text-xs font-semibold tabular-nums text-content-primary">
        {tieuDe}
      </p>
      <ul className="space-y-1">
        {dong.map((d) => (
          <li
            key={d.nhan}
            className={
              d.dam
                ? 'mt-1.5 flex items-baseline gap-2 border-t border-default pt-1.5 text-xs font-semibold text-content-primary'
                : 'flex items-baseline gap-2 text-xs text-content-secondary'
            }
          >
            <span
              className="inline-block size-2 shrink-0 translate-y-[-1px] rounded-sm"
              style={{ backgroundColor: d.mau ?? 'transparent' }}
            />
            <span className="min-w-0 flex-1 break-words">{d.nhan}</span>
            <span className="shrink-0 text-right tabular-nums text-content-primary">{d.giaTri}</span>
            {d.phu !== undefined ? (
              <span className="w-12 shrink-0 text-right tabular-nums text-content-muted">{d.phu}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Mục nhận rê và focus: cột SVG, hoặc dòng HTML của bảng xếp hạng. */
type MucTuongTac = Element & HTMLOrSVGElement;

/**
 * Rê, chạm và bàn phím cho một dãy mục (cột, lát vành) — một chỗ cho ba biểu đồ.
 *
 * • Bàn phím: cả dãy là MỘT điểm dừng Tab (roving tabindex); mũi tên trái/phải (hoặc lên/xuống), Home, End đi giữa các
 *   mục, Esc giấu thẻ. Focus vào mục là hiện thẻ; vòng focus chỉ vẽ khi focus đến từ bàn phím (`:focus-visible`).
 * • Chạm: chạm vào mục là hiện thẻ (mục nhận focus); chạm ra ngoài là mất focus và thẻ ẩn. Không ẩn ở
 *   `pointerleave` của ngón tay — trình duyệt bắn nó ngay sau khi nhấc tay, thẻ vừa hiện đã tắt.
 */
export function useMucTuongTac(so: number) {
  const [dang, setDang] = useState<number | null>(null);
  const [vongFocus, setVongFocus] = useState<number | null>(null);
  const [diemDung, setDiemDung] = useState(0);
  const cacMuc = useRef<(MucTuongTac | null)[]>([]);

  // Chuột rời biểu đồ: về mục đang focus bằng bàn phím nếu có, không thì giấu thẻ.
  const roiKhoi = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') setDang(vongFocus);
  };

  const thuocTinh = (i: number) => ({
    ref: (el: MucTuongTac | null) => {
      cacMuc.current[i] = el;
    },
    tabIndex: i === Math.min(diemDung, Math.max(0, so - 1)) ? 0 : -1,
    className: 'outline-none',
    onPointerEnter: () => setDang(i),
    onPointerDown: () => setDang(i),
    onFocus: (e: FocusEvent<MucTuongTac>) => {
      setDang(i);
      setDiemDung(i);
      setVongFocus(e.currentTarget.matches(':focus-visible') ? i : null);
    },
    onBlur: () => {
      setDang(null);
      setVongFocus(null);
    },
    onKeyDown: (e: KeyboardEvent<MucTuongTac>) => {
      const toi = (j: number) => {
        e.preventDefault();
        cacMuc.current[kep(j, 0, so - 1)]?.focus();
      };
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') toi(i + 1);
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') toi(i - 1);
      else if (e.key === 'Home') toi(0);
      else if (e.key === 'End') toi(so - 1);
      else if (e.key === 'Escape') setDang(null);
    },
  });

  return { dang, vongFocus, roiKhoi, thuocTinh, cacMuc };
}

const soTheo = (gt: number, locale: string) => gt.toLocaleString(locale);
const TY_LE_NHO_NHAT = 0.1;

/** Phần có thật nhưng dưới 0,1% hiện "<0,1%": "0%" đọc ra là không có. */
export function phanTram(phan: number, tren: number, locale: string): string {
  if (tren <= 0) return '';
  const ty = (phan / tren) * 100;
  if (phan > 0 && ty < TY_LE_NHO_NHAT) return `<${TY_LE_NHO_NHAT.toLocaleString(locale)}%`;
  return `${ty.toLocaleString(locale, { maximumFractionDigits: 1 })}%`;
}

const RONG_MAC_DINH = 760;
const RONG_TOI_THIEU = 320;

/**
 * Bề rộng THẬT của khung chứa, tính bằng px.
 *
 * Bản đầu vẽ trong một `viewBox` rộng cố định 760 rồi để SVG co giãn theo khung. Hệ quả: biểu đồ
 * chiếm cả hàng (rộng gấp đôi) thì MỌI thứ phóng to gấp đôi, kể cả chữ trục — cùng một cỡ chữ 10 mà
 * biểu đồ lớn hiện chữ 20px còn biểu đồ nhỏ hiện chữ 8px, trên cùng một màn hình. Vẽ theo bề rộng
 * thật thì một đơn vị luôn là một px và chữ ở đâu cũng một cỡ.
 */
function useRongKhung() {
  const [rong, setRong] = useState(RONG_MAC_DINH);
  // Ref gọi lại có hàm dọn (React 19) thay cho `useEffect`: quan sát gắn đúng lúc phần tử vào DOM và gỡ khi nó ra.
  const khung = useCallback((el: HTMLDivElement | null) => {
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const quanSat = new ResizeObserver(([muc]) => {
      const moi = Math.round(muc?.contentRect.width ?? 0);
      if (moi > 0) setRong(Math.max(RONG_TOI_THIEU, moi));
    });
    quanSat.observe(el);
    return () => quanSat.disconnect();
  }, []);
  return { khung, rong };
}
const LE_TRAI = 46;
const LE_PHAI = 8;
const LE_TREN = 10;
const LE_DUOI = 24;
const BAC_LUOI = 4;
const RONG_KY_TU_NHAN = 6.2;
const KHE_NHAN = 12;

/**
 * Cứ mấy cột thì ghi một nhãn trục X để hai nhãn cạnh nhau không đè lên nhau.
 * Tính theo bề rộng thật: cùng 20 cột, biểu đồ nửa hàng chỉ đủ chỗ cho nửa số
 * nhãn của biểu đồ cả hàng.
 */
function buocNhan(cot: { nhan: string }[], buocCot: number): number {
  const daiNhat = cot.reduce((a, c) => Math.max(a, c.nhan.length), 0);
  return Math.max(1, Math.ceil((daiNhat * RONG_KY_TU_NHAN + KHE_NHAN) / Math.max(buocCot, 1)));
}

/** Mốc trục đẹp — 1/2/5 × 10ⁿ. Chia đều khoảng cho ra những mốc như `3.847`. */
function tranDep(gt: number): number {
  if (gt <= 0) return 1;
  const bac = 10 ** Math.floor(Math.log10(gt));
  for (const he of [1, 2, 2.5, 5, 10]) {
    if (he * bac >= gt) return he * bac;
  }
  return 10 * bac;
}

/**
 * Trần của trục đếm (token, lượt, sự cố): bậc lưới là số NGUYÊN đẹp (1, 2, 5 × 10ⁿ), nên vạch trục không bao giờ là
 * `0,5` hay `12,5` — không có nửa sự cố. Trần có thể cao hơn `tranDep` một chút, đổi lại mọi vạch là số đếm được.
 */
function tranDem(gt: number): number {
  const can = Math.max(1, gt) / BAC_LUOI;
  for (let bac = 10 ** Math.max(0, Math.floor(Math.log10(can))); ; bac *= 10) {
    for (const he of [1, 2, 5]) {
      if (he * bac >= can) return he * bac * BAC_LUOI;
    }
  }
}

export interface CotXepChong {
  /** Nhãn trục X. */
  nhan: string;
  /** Tiêu đề thẻ chú thích, đầy đủ hơn nhãn trục (`10:00–11:00 · 02/11/2026`). Bỏ trống thì dùng `nhan`. */
  tieuDe?: string;
  /** Giá trị từng lát, cùng thứ tự với `loat`. */
  lat: number[];
  /** Tổng mà bảy lát PHẢI cộng ra — `tong_token` của dòng, không tính lại. */
  tong: number;
  /** Câu đầy đủ cho trình đọc màn hình, nơi gọi dựng vì nó có i18n. */
  moTa: string;
  /** Dòng thêm dưới dòng tổng của thẻ chú thích (số lượt). */
  theThem?: DongThe[];
}

export interface Loat {
  nhan: string;
  mau: string;
}

/**
 * Cột xếp chồng — bảy lát token của mục 8.2.
 *
 * Chiều cao cột lấy theo `tong` của dòng, KHÔNG theo tổng các lát: hai số ấy
 * phải bằng nhau (QĐ-14 là một phân hoạch) và vẽ theo `tong` là cách duy nhất
 * để lệch hiện ra thành một khe trắng ở đỉnh cột thay vì bị che. Nơi gọi còn
 * kiểm cùng điều đó bằng số, ở dòng dưới biểu đồ.
 */
export function BieuDoXepChong({
  cot,
  loat,
  nhan,
  nhanTong,
  dinhDang: dinhDangRieng,
  cao = 200,
  hienSoKhong = false,
}: {
  cot: CotXepChong[];
  /** Thẻ chú thích giữ cả lát bằng 0: khi số lát ít, "đầu ra 0" là một câu trả lời, không phải chỗ thừa. */
  hienSoKhong?: boolean;
  loat: Loat[];
  nhan: string;
  /** Nhãn dòng tổng trong thẻ chú thích. Không truyền thì thẻ không có dòng tổng. */
  nhanTong?: string;
  /** Định dạng số trong thẻ, kèm đơn vị (`1.234 token`). Bỏ trống thì chỉ số. */
  dinhDang?: (gt: number) => string;
  cao?: number;
}) {
  const { locale } = useT();
  const { khung, rong: RONG } = useRongKhung();
  const so = cot.length;
  const { dang, vongFocus, roiKhoi, thuocTinh } = useMucTuongTac(so);
  const khe = so > 20 ? 1.5 : 3;
  const rongCot = (RONG - LE_TRAI - LE_PHAI - Math.max(0, so - 1) * khe) / Math.max(1, so);
  const tran = tranDem(Math.max(1, ...cot.map((c) => c.tong)));
  const dinhDang = dinhDangRieng ?? ((gt: number) => soTheo(gt, locale));
  const trong = cao - LE_TREN - LE_DUOI;
  const cuNhan = buocNhan(cot, rongCot + khe);
  const cotDang = dang === null ? undefined : cot[dang];

  return (
    <div ref={khung} className="relative w-full">
      <svg
        width={RONG}
        height={cao}
        viewBox={`0 0 ${RONG} ${cao}`}
        className="block max-w-full text-content-muted"
        role="group"
        aria-label={nhan}
        onPointerLeave={roiKhoi}
      >
        {Array.from({ length: BAC_LUOI + 1 }, (_, i) => {
          const y = LE_TREN + (trong * i) / BAC_LUOI;
          return (
            <g key={i}>
              <line
                x1={LE_TRAI}
                y1={y}
                x2={RONG - LE_PHAI}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.15}
              />
              <text x={LE_TRAI - 6} y={y + 3.5} textAnchor="end" fontSize={11} fill="currentColor">
                {gonSo((tran * (BAC_LUOI - i)) / BAC_LUOI, locale)}
              </text>
            </g>
          );
        })}
        {cot.map((c, i) => {
          const x = LE_TRAI + i * (rongCot + khe);
          let day = LE_TREN + trong;
          return (
            <g key={c.nhan} role="img" aria-label={c.tieuDe ?? c.nhan} {...thuocTinh(i)}>
              <desc>{c.moTa}</desc>
              <VungCot x={x} rongCot={rongCot} khe={khe} trong={trong} dangRe={dang === i} vongFocus={vongFocus === i} />
              <g opacity={dang === null || dang === i ? 1 : MO_COT_KHAC} className="transition-opacity">
                {c.lat.map((gt, j) => {
                  const h = gt > 0 ? (gt / tran) * trong : 0;
                  day -= h;
                  return h > 0 ? (
                    <rect
                      key={loat[j]?.nhan ?? j}
                      x={x}
                      y={day}
                      width={rongCot}
                      height={h}
                      fill={loat[j]?.mau ?? 'currentColor'}
                    />
                  ) : null;
                })}
              </g>
              {i % cuNhan === 0 ? (
                <text
                  x={x + rongCot / 2}
                  y={cao - 8}
                  textAnchor="middle"
                  fontSize={11}
                  fill="currentColor"
                >
                  {c.nhan}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {cotDang && dang !== null ? (
        <TheChuThich
          key={dang}
          tieuDe={cotDang.tieuDe ?? cotDang.nhan}
          neo={neoCot(dang, rongCot, khe)}
          khung={{ rong: RONG, cao }}
          dong={[
            ...loat
              .map((l, j) => ({ l, gt: cotDang.lat[j] ?? 0 }))
              .filter(({ gt }) => hienSoKhong || gt > 0)
              .map(({ l, gt }) => ({
                nhan: l.nhan,
                mau: l.mau,
                giaTri: dinhDang(gt),
                phu: phanTram(gt, cotDang.tong, locale),
              })),
            ...(nhanTong
              ? [{ nhan: nhanTong, giaTri: dinhDang(cotDang.tong), phu: '', dam: true }]
              : []),
            ...(cotDang.theThem ?? []),
          ]}
        />
      ) : null}
    </div>
  );
}

const MO_COT_KHAC = 0.45;

/**
 * Vùng bắt chuột và dải nền của một cột — cao hết vùng vẽ, rộng cả khe hai bên.
 * Không có nó thì cột chỉ vài px (giờ ít lượt gọi) gần như không rê trúng được.
 */
function VungCot({
  x,
  rongCot,
  khe,
  trong,
  dangRe,
  vongFocus,
}: {
  x: number;
  rongCot: number;
  khe: number;
  trong: number;
  dangRe: boolean;
  /** Vòng focus của bàn phím — `outline` không ăn theo hình của phần tử SVG nên tự vẽ. */
  vongFocus: boolean;
}) {
  return (
    <rect
      x={x - khe / 2}
      y={LE_TREN}
      width={rongCot + khe}
      height={trong}
      rx={3}
      fill="currentColor"
      fillOpacity={dangRe ? 0.1 : 0}
      stroke={vongFocus ? 'var(--color-primary-600)' : 'none'}
      strokeWidth={2}
    />
  );
}

/** Đoạn ngang của cột thứ `i` — thẻ chú thích tránh đoạn này. */
function neoCot(i: number, rongCot: number, khe: number): NeoThe {
  const x = LE_TRAI + i * (rongCot + khe);
  return { trai: x - khe / 2, phai: x + rongCot + khe / 2 };
}

export interface CotDon {
  nhan: string;
  /** Như `CotXepChong.tieuDe`. */
  tieuDe?: string;
  giaTri: number;
  moTa: string;
  /** Các dòng của thẻ chú thích. Không truyền thì thẻ hiện một dòng giá trị theo `dinhDang`. */
  the?: DongThe[];
}

/**
 * Cột đơn, CÓ CHỖ CHO SỐ ÂM.
 *
 * `tiet_kiem_dem` được phép âm (QĐ-15) và một trục bắt đầu từ 0 sẽ vẽ nó thành
 * cột cao 0 — tức giấu đúng cái tín hiệu cần đọc. Nên trục ở đây tự nới xuống
 * dưới 0 khi có giá trị âm, và đường 0 được vẽ đậm hơn lưới để người đọc thấy
 * cột nào treo bên dưới nó.
 */
export function BieuDoCot({
  cot,
  nhan,
  mau = MAU_TIEN,
  mauAm = MAU_AM,
  dinhDang: dinhDangRieng,
  cao = 170,
}: {
  cot: CotDon[];
  nhan: string;
  mau?: string;
  mauAm?: string;
  /** Bỏ trống thì gọn số theo ngôn ngữ đang xem (`gonSo`). */
  dinhDang?: (gt: number) => string;
  cao?: number;
}) {
  const { locale } = useT();
  const dinhDang = dinhDangRieng ?? ((gt: number) => gonSo(gt, locale));
  const { khung, rong: RONG } = useRongKhung();
  const so = cot.length;
  const { dang, vongFocus, roiKhoi, thuocTinh } = useMucTuongTac(so);
  const khe = so > 20 ? 1.5 : 3;
  const rongCot = (RONG - LE_TRAI - LE_PHAI - Math.max(0, so - 1) * khe) / Math.max(1, so);
  const gia = cot.map((c) => c.giaTri);
  const tran = tranDep(Math.max(0, ...gia));
  const thapNhat = Math.min(0, ...gia);
  const san = thapNhat < 0 ? -tranDep(-thapNhat) : 0;
  const nhip = tran - san || 1;
  const trong = cao - LE_TREN - LE_DUOI;
  const yCua = (gt: number) => LE_TREN + ((tran - gt) / nhip) * trong;
  const yKhong = yCua(0);
  const cuNhan = buocNhan(cot, rongCot + khe);
  const cotDang = dang === null ? undefined : cot[dang];

  return (
    <div ref={khung} className="relative w-full">
      <svg
        width={RONG}
        height={cao}
        viewBox={`0 0 ${RONG} ${cao}`}
        className="block max-w-full text-content-muted"
        role="group"
        aria-label={nhan}
        onPointerLeave={roiKhoi}
      >
        {Array.from({ length: BAC_LUOI + 1 }, (_, i) => {
          const gt = tran - (nhip * i) / BAC_LUOI;
          const y = yCua(gt);
          return (
            <g key={i}>
              <line
                x1={LE_TRAI}
                y1={y}
                x2={RONG - LE_PHAI}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.15}
              />
              <text x={LE_TRAI - 6} y={y + 3.5} textAnchor="end" fontSize={11} fill="currentColor">
                {dinhDang(gt)}
              </text>
            </g>
          );
        })}
        {san < 0 ? (
          <line
            x1={LE_TRAI}
            y1={yKhong}
            x2={RONG - LE_PHAI}
            y2={yKhong}
            stroke="currentColor"
            strokeOpacity={0.55}
          />
        ) : null}
        {cot.map((c, i) => {
          const x = LE_TRAI + i * (rongCot + khe);
          const y = yCua(Math.max(0, c.giaTri));
          const h = Math.abs(yCua(c.giaTri) - yKhong);
          return (
            <g key={c.nhan} role="img" aria-label={c.tieuDe ?? c.nhan} {...thuocTinh(i)}>
              <desc>{c.moTa}</desc>
              <VungCot x={x} rongCot={rongCot} khe={khe} trong={trong} dangRe={dang === i} vongFocus={vongFocus === i} />
              <rect
                x={x}
                y={c.giaTri >= 0 ? y : yKhong}
                width={rongCot}
                height={Math.max(h, c.giaTri === 0 ? 0 : 1)}
                fill={c.giaTri < 0 ? mauAm : mau}
                opacity={dang === null || dang === i ? 1 : MO_COT_KHAC}
                className="transition-opacity"
              />
              {i % cuNhan === 0 ? (
                <text
                  x={x + rongCot / 2}
                  y={cao - 8}
                  textAnchor="middle"
                  fontSize={11}
                  fill="currentColor"
                >
                  {c.nhan}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {cotDang && dang !== null ? (
        <TheChuThich
          key={dang}
          tieuDe={cotDang.tieuDe ?? cotDang.nhan}
          neo={neoCot(dang, rongCot, khe)}
          khung={{ rong: RONG, cao }}
          dong={
            cotDang.the ?? [
              {
                nhan,
                giaTri: dinhDang(cotDang.giaTri),
                mau: cotDang.giaTri < 0 ? mauAm : mau,
              },
            ]
          }
        />
      ) : null}
    </div>
  );
}

/** Chú giải — nhãn, màu, và con số của chính lát ấy nếu nơi gọi có. */
export function ChuGiai({
  muc,
}: {
  muc: { nhan: string; mau: string; phu?: string; moTa?: string }[];
}) {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-content-secondary">
      {muc.map((m) => (
        <li key={m.nhan} className="inline-flex items-center gap-1.5" title={m.moTa}>
          <span
            aria-hidden
            className="inline-block size-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: m.mau }}
          />
          <span>{m.nhan}</span>
          {m.phu ? <span className="tabular-nums text-content-muted">{m.phu}</span> : null}
        </li>
      ))}
    </ul>
  );
}

export interface LatVanh {
  nhan: string;
  giaTri: number;
  mau: string;
}

const VANH_CO = 148;
const VANH_DAY = 18;

/** Vành tỷ lệ. Con số giữa vành là chữ HTML chứ không `<text>` để ăn theo token màu chữ của trang. */
export function BieuDoVanh({
  lat,
  nhan,
  giua,
  phu,
  dinhDang,
}: {
  lat: LatVanh[];
  nhan: string;
  giua: string;
  phu?: string;
  /** Định dạng số trong thẻ, kèm đơn vị. Bỏ trống thì chỉ số. */
  dinhDang?: (gt: number) => string;
}) {
  const { locale } = useT();
  const tong = lat.reduce((a, l) => a + Math.max(0, l.giaTri), 0);
  const r = (VANH_CO - VANH_DAY) / 2;
  const chuVi = 2 * Math.PI * r;
  const tam = VANH_CO / 2;
  const khung = useRef<HTMLDivElement>(null);

  // Lát có thật (dài > 0), kèm đoạn của nó trên chu vi.
  let chay = 0;
  const cacLat = (tong > 0 ? lat : []).flatMap((l) => {
    const dai = (Math.max(0, l.giaTri) / tong) * chuVi;
    const lech = chay;
    chay += dai;
    return dai > 0 ? [{ l, dai, lech }] : [];
  });
  const { dang, vongFocus, roiKhoi, thuocTinh } = useMucTuongTac(cacLat.length);
  const latDang = dang === null ? undefined : cacLat[dang];

  /*
   * Vành chỉ rộng 148px nên thẻ đứng NGOÀI vành, cạnh bên có chỗ trong màn hình, canh dọc theo giữa lát đang rê —
   * không đi theo con trỏ (thẻ nhảy theo từng px và đè lên chính lát ấy). Miền ngang tính theo viewport lúc vẽ.
   */
  const o = khung.current?.getBoundingClientRect();
  const mien = o
    ? { trai: LE_MAN_HINH - o.left, rong: window.innerWidth - LE_MAN_HINH - o.left, cao: VANH_CO }
    : { trai: 0, rong: VANH_CO, cao: VANH_CO };
  const giuaLat = latDang ? (latDang.lech + latDang.dai / 2) / chuVi : 0;
  const neo: NeoThe = { trai: 0, phai: VANH_CO, y: tam - r * Math.cos(2 * Math.PI * giuaLat) };

  const rVong = r + VANH_DAY / 2 + 3;
  return (
    <div ref={khung} className="relative shrink-0" style={{ width: VANH_CO, height: VANH_CO }}>
      <svg
        width={VANH_CO}
        height={VANH_CO}
        viewBox={`0 0 ${VANH_CO} ${VANH_CO}`}
        className="block -rotate-90 text-content-muted"
        role="group"
        aria-label={nhan}
        onPointerLeave={roiKhoi}
      >
        <circle cx={tam} cy={tam} r={r} fill="none" stroke="currentColor" strokeOpacity={0.12} strokeWidth={VANH_DAY} />
        {cacLat.map(({ l, dai, lech }, i) => (
          <circle
            key={l.nhan}
            cx={tam}
            cy={tam}
            r={r}
            fill="none"
            stroke={l.mau}
            strokeWidth={VANH_DAY}
            strokeDasharray={`${dai} ${chuVi - dai}`}
            strokeDashoffset={-lech}
            opacity={dang === null || dang === i ? 1 : MO_COT_KHAC}
            role="img"
            aria-label={`${l.nhan}: ${soTheo(l.giaTri, locale)} (${phanTram(l.giaTri, tong, locale)})`}
            {...thuocTinh(i)}
            className="outline-none transition-opacity"
          >
            <desc>{`${l.nhan} — ${soTheo(l.giaTri, locale)}`}</desc>
          </circle>
        ))}
        {latDang && vongFocus === dang ? (
          // Vòng focus: một cung mảnh ôm ngoài lát, cùng góc.
          <circle
            aria-hidden
            cx={tam}
            cy={tam}
            r={rVong}
            fill="none"
            stroke="var(--color-primary-600)"
            strokeWidth={2}
            strokeDasharray={`${(latDang.dai * rVong) / r} ${2 * Math.PI * rVong}`}
            strokeDashoffset={(-latDang.lech * rVong) / r}
            pointerEvents="none"
          />
        ) : null}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-semibold tabular-nums text-content-primary">{giua}</span>
        {phu ? <span className="text-xs text-content-muted">{phu}</span> : null}
      </div>
      {latDang && dang !== null ? (
        <TheChuThich
          key={dang}
          tieuDe={nhan}
          neo={neo}
          khung={mien}
          dong={[
            {
              nhan: latDang.l.nhan,
              mau: latDang.l.mau,
              giaTri: (dinhDang ?? ((gt: number) => soTheo(gt, locale)))(latDang.l.giaTri),
              phu: phanTram(latDang.l.giaTri, tong, locale),
            },
          ]}
        />
      ) : null}
    </div>
  );
}

/** Khoảng chừa ở mép màn hình khi thẻ chú thích đứng ngoài khung của nó (vành). */
const LE_MAN_HINH = 16;

/** Thanh ngang tỷ lệ của một dòng trong danh sách xếp hạng. */
export function ThanhNgang({ phan, tren, mau }: { phan: number; tren: number; mau: string }) {
  const ty = tren > 0 ? Math.min(Math.max(phan / tren, 0), 1) : 0;
  return (
    <span aria-hidden className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-subtle">
      <span
        className="block h-full rounded-full"
        style={{ width: `${Math.max(ty * 100, phan > 0 ? 1.5 : 0)}%`, backgroundColor: mau }}
      />
    </span>
  );
}
